'use client';

import { useEffect, useRef } from 'react';

type Props = {
  label: string;
};

const CASE_CONTENT_SELECTOR = '[data-industrial-case-content]';

export default function IndustrialCaseScrollProgress({ label }: Props) {
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const progress = progressRef.current;
    const content = progress?.closest<HTMLElement>(CASE_CONTENT_SELECTOR);
    if (!progress || !content) return;

    let frame: number | null = null;

    const updateProgress = () => {
      frame = null;

      const contentTop = window.scrollY + content.getBoundingClientRect().top;
      const scrollDistance = Math.max(
        content.offsetHeight - window.innerHeight,
        1,
      );
      const value = Math.min(
        Math.max((window.scrollY - contentTop) / scrollDistance, 0),
        1,
      );

      progress.style.transform = `scaleX(${value})`;
      progress.setAttribute('aria-valuenow', String(Math.round(value * 100)));
    };

    const requestProgressUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(updateProgress);
    };

    const resizeObserver = new ResizeObserver(requestProgressUpdate);
    resizeObserver.observe(content);
    updateProgress();

    window.addEventListener('scroll', requestProgressUpdate, { passive: true });
    window.addEventListener('resize', requestProgressUpdate);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', requestProgressUpdate);
      window.removeEventListener('resize', requestProgressUpdate);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[1000] h-[3px]">
      <div
        ref={progressRef}
        className="h-full origin-left scale-x-0 bg-apps-industrialAccent will-change-transform"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={0}
      />
    </div>
  );
}
